// src/components/ui/TirerPourRecharger.tsx — SCE Issues
// Téléphone (retour du 09/10) : tirer la page vers le bas, depuis le haut,
// recharge TOUTE l'application, comme Ctrl+Maj+R sur PC : nouvelle version du
// site comprise, pas seulement les listes (le bouton ⟳ de l'en-tête fait ça).
//
// Le rebond natif de Safari est coupé (globals.css : overscroll-behavior) :
// il montrait le fond gris de la page au-dessus de l'en-tête jaune. Ici, toute
// la page descend avec le doigt et découvre au-dessus de l'en-tête le fond
// jaune (celui de <html>), où s'affiche la consigne.
//
// Animation (retours du 09/10) : on peut continuer à tirer, la page est
// élastique (elle résiste de plus en plus, sans butée). Une grande flèche
// s'étire avec le pouce, comme un élastique : grise, puis noire au seuil (avec
// un petit rebond, et une vibration sur Android). Au relâché, elle se replie
// et devient une flèche circulaire qui tourne pendant le rechargement.
// (Pas de signet « S » Sartorius ici : réservé à la marque.)
//
// Ne se déclenche que si la page est tout en haut, et jamais depuis une
// fenêtre par-dessus (visionneuse, annotation, menus) ni depuis une zone qui
// défile elle-même.

import { useEffect, useRef, useState } from 'react'
import { RotateCw } from 'lucide-react'
import { useMediaQuery } from '@/hooks/useMediaQuery'

/** Distance tirée (après résistance) qui déclenche le rechargement. */
const SEUIL_PX = 90
/** Élastique : la page tend vers cette distance sans jamais l'atteindre. */
const LIMITE_PX = 340

/** Déplacement du doigt → descente de la page : presque 2 pour 3 au début, puis de moins en moins. */
export function elastique(doigt: number): number {
  if (doigt <= 0) return 0
  return LIMITE_PX * (1 - Math.exp(-doigt / (LIMITE_PX * 1.6)))
}

/**
 * Rechargement « forcé » : on vide les caches du navigateur que l'application
 * peut atteindre, on redemande la page au serveur sans cache, puis on
 * recharge. Les fichiers du site ont un nom qui change à chaque version : une
 * page neuve suffit à tout reprendre.
 */
export async function rechargerToutLApplication(): Promise<void> {
  try {
    if ('caches' in window) {
      const cles = await caches.keys()
      await Promise.all(cles.map((c) => caches.delete(c)))
    }
  } catch {
    // Pas de cache à vider : rien de bloquant.
  }
  try {
    await fetch(window.location.href, { cache: 'reload', credentials: 'same-origin' })
  } catch {
    // Hors ligne : le rechargement dira de lui-même ce qu'il en est.
  }
  window.location.reload()
}

/** La page (et non une zone intérieure) est-elle tout en haut, sous ce doigt ? */
function departPossible(cible: EventTarget | null): boolean {
  if ((document.scrollingElement?.scrollTop ?? window.scrollY) > 0) return false
  let el = cible instanceof Element ? cible : null
  // Fenêtre par-dessus la page : ses gestes à elle.
  if (el?.closest('[role="dialog"], [aria-modal="true"], input, textarea, select, canvas')) return false
  while (el && el !== document.body) {
    const style = getComputedStyle(el)
    if (/(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight && el.scrollTop > 0) return false
    el = el.parentElement
  }
  return true
}

export function TirerPourRecharger(): JSX.Element | null {
  const tactile = useMediaQuery('(pointer: coarse)')
  const [distance, setDistance] = useState(0)
  const [recharge, setRecharge] = useState(false)
  // Au relâché : la flèche se replie, puis tourne.
  const [replie, setReplie] = useState(false)
  const depart = useRef<{ y: number; x: number; actif: boolean } | null>(null)
  const distanceRef = useRef(0)
  const [tire, setTire] = useState(false)

  // La page descend avec le doigt ; au relâché, elle remonte en douceur.
  useEffect(() => {
    const racine = document.getElementById('root')
    if (!racine) return
    racine.style.transition = tire ? 'none' : 'margin-top 0.2s ease-out'
    racine.style.marginTop = distance > 0 ? `${distance}px` : ''
  }, [distance, tire])

  useEffect(() => {
    if (!tactile) return undefined

    function onStart(e: TouchEvent): void {
      if (e.touches.length !== 1 || recharge) return
      const t = e.touches[0]!
      depart.current = departPossible(e.target) ? { y: t.clientY, x: t.clientX, actif: false } : null
    }

    function onMove(e: TouchEvent): void {
      const d = depart.current
      const t = e.touches[0]
      if (!d || !t) return
      const dy = t.clientY - d.y
      const dx = t.clientX - d.x
      if (!d.actif) {
        // Un geste horizontal (carrousel, tableau) ou vers le haut n'est pas le nôtre.
        if (Math.abs(dx) > Math.abs(dy) || dy <= 4) {
          if (Math.abs(dx) > 8 || dy < -4) depart.current = null
          return
        }
        d.actif = true
        setTire(true)
      }
      const tire = elastique(dy)
      // Seuil franchi (dans un sens ou l'autre) : un petit « tic » sous le doigt.
      if ((distanceRef.current < SEUIL_PX) !== (tire < SEUIL_PX)) navigator.vibrate?.(8)
      distanceRef.current = tire
      setDistance(tire)
      if (e.cancelable) e.preventDefault()
    }

    function onEnd(): void {
      const d = depart.current
      depart.current = null
      if (!d?.actif) return
      setTire(false)
      if (distanceRef.current >= SEUIL_PX) {
        setRecharge(true)
        window.setTimeout(() => setReplie(true), 260)
        setDistance(SEUIL_PX)
        void rechargerToutLApplication()
      } else {
        distanceRef.current = 0
        setDistance(0)
      }
    }

    // touchmove non passif : empêcher le défilement pendant qu'on tire.
    window.addEventListener('touchstart', onStart, { passive: true })
    window.addEventListener('touchmove', onMove, { passive: false })
    window.addEventListener('touchend', onEnd)
    window.addEventListener('touchcancel', onEnd)
    return () => {
      window.removeEventListener('touchstart', onStart)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
      window.removeEventListener('touchcancel', onEnd)
    }
  }, [tactile, recharge])

  if (!tactile || distance <= 0) return null
  const pret = distance >= SEUIL_PX
  const consigne = recharge
    ? 'Rechargement de l’application…'
    : !pret
      ? 'Tirez pour tout recharger'
      : distance > 180
        ? 'Relâchez, ça repart comme neuf'
        : 'Relâchez pour tout recharger'

  // La flèche occupe la zone découverte, au-dessus de la consigne. Plus on
  // tire, plus elle s'allonge et s'affine, comme un élastique qu'on tend.
  const hauteurFleche = Math.max(0, distance - 40)
  const epaisseur = Math.max(2.5, 5 - distance / 110)
  const pointe = 11

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={consigne}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 70,
        // Exactement la zone découverte au-dessus de la page.
        height: distance,
        background: 'var(--color-yellow)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 4,
        paddingBottom: 10,
        boxSizing: 'border-box',
        transition: tire ? 'none' : 'height 0.25s cubic-bezier(0.22, 0.8, 0.3, 1)',
        pointerEvents: 'none',
        color: 'var(--color-black)',
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          width: 40,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          paddingTop: 6,
        }}
      >
        {replie ? (
          // Repliée : la flèche devient circulaire et tourne.
          <span className="tirer-apparait" style={{ display: 'flex', marginBottom: 2 }}>
            <RotateCw size={30} strokeWidth={2.6} className="tirer-tourne" aria-hidden="true" />
          </span>
        ) : (
          <span
            // La clé change au seuil : le rebond se rejoue.
            key={pret ? 'pret' : 'tire'}
            className={pret && !recharge ? 'tirer-rebond-pointe' : undefined}
            style={{
              display: 'block',
              width: 40,
              height: hauteurFleche,
              // Au relâché, la flèche se rétracte vers sa pointe.
              transform: recharge ? 'scaleY(0.12)' : 'none',
              transformOrigin: 'bottom center',
              transition: recharge ? 'transform 0.25s cubic-bezier(0.5, 0, 0.75, 0)' : 'none',
              opacity: Math.min(1, distance / 30),
            }}
          >
            <svg width="40" height={hauteurFleche} aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
              <g
                stroke={pret ? 'var(--color-black)' : 'rgba(0,0,0,0.4)'}
                strokeWidth={epaisseur}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                style={{ transition: 'stroke 0.15s' }}
              >
                <line x1="20" y1="2" x2="20" y2={Math.max(3, hauteurFleche - 2)} />
                <polyline
                  points={`${20 - pointe},${hauteurFleche - 2 - pointe} 20,${hauteurFleche - 2} ${20 + pointe},${hauteurFleche - 2 - pointe}`}
                />
              </g>
            </svg>
          </span>
        )}
      </span>
      <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', opacity: Math.min(1, Math.max(0, (distance - 40) / 30)) }}>
        {consigne}
      </span>
    </div>
  )
}
